<template>
    <div class="splash-screen" :class="{ 'no-hover': !isStylePreview && !isPreview, 'inline-edit-mode': enableInlineEdit }" :style="densityVars">
        <div class="center-wrapper" :style="centerWrapperStyles">
            <div class="frame" :style="frameStyles">
            <div v-if="showBackButton" class="back-btn" :style="backBtnStyles">
                <div class="back-icon-wrapper" :style="backIconStyles">
                    <component :is="icons.back2" class="back-icon" :color="backIconColor" />
                </div>
            </div>

            <div class="card" :style="cardStyles">
                <div class="card-grid" :style="cardGridStyles">
                    <div class="copy">
                    <h1
                        v-show="true"
                        class="splash-title editable-text"
                        :class="{ 'is-editable': enableInlineEdit }"
                        :style="titleStyles"
                        :contenteditable="enableInlineEdit"
                        @blur="updateTitle"
                        @input="(e) => handleTextFieldInput('titleText', e)"
                        @paste.prevent="handlePaste"
                        @mousedown="handleEditableMouseDown"
                        @keydown.enter.prevent="handleEnter"
                        suppressContentEditableWarning
                        ref="titleElement"
                    >{{ displayTitleText }}</h1>
                        <p
                            v-show="true"
                            ref="bodyElement"
                            class="body editable-text"
                            :class="{ 'is-editable': enableInlineEdit }"
                            :style="bodyStyles"
                            :contenteditable="enableInlineEdit"
                            @blur="updateBody"
                            @input="(e) => handleTextFieldInput('bodyText', e)"
                            @paste.prevent="handlePaste"
                            @mousedown="handleEditableMouseDown"
                            @keydown.enter.prevent="handleEnter"
                            suppressContentEditableWarning
                        >{{ displayBodyText }}</p>
                    </div>

                    <div
                        class="placeholder"
                        :class="{ 'image-clickable': enableInlineEdit && (!imageData.imageType || imageData.imageType === 'image') }"
                        :style="getImagePlaceholderStyle()"
                        @click="enableInlineEdit && (!imageData.imageType || imageData.imageType === 'image') ? handleImageClick() : null"
                    >
                        <div v-if="imageData.imageType === 'icon' && imageData.selectedIcon" class="icon-display">
                            <component :is="getIconComponent(imageData.selectedIcon)" :style="iconDisplayStyles" class="icon-img" v-if="getIconComponent(imageData.selectedIcon)" />
                        </div>
                        <div v-else-if="(imageData.imageType === 'image' || imageData.imageType === 'resource') && imageData.imageUrl" class="image-display">
                        </div>
                        <div v-else class="ph-text" :style="placeholderTextStyles">
                            {{ placeholderText }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="buttons" :style="buttonsRowStyles">
                <div
                    v-for="(btn, i) in buttons"
                    :key="i"
                    v-show="btn.enabled !== false"
                    :ref="`buttonRef${i}`"
                    class="btn editable-text"
                    :class="{ 'is-editable': enableInlineEdit }"
                    :style="getButtonStyles(btn)"
                    :contenteditable="enableInlineEdit && btn.enabled !== false"
                    @blur="(e) => updateButton(i, e)"
                    @input="(e) => handleTextFieldInput(`buttonText${i}`, e)"
                    @click="handleButtonClick(i, btn)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ i === 0 ? displayButtonText0 : i === 1 ? displayButtonText1 : displayButtonText2 }}</div>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import icons from './menu-icons';
import menuMixin from './menu-mixin';
import menuReactivityMixin from './menu-reactivity-mixin';
const Back2SVG = require('!raw-loader!../assets/icons/back2.svg');

export default {
    name: "MenuSplashScreen",
    mixins: [menuMixin, menuReactivityMixin],
    props: {
        styleData: {type: Object, default: () => ({})},
        menuData: {type: Object, default: () => ({})},
        isStylePreview: {type: Boolean, default: false},
        isPreview: {type: Boolean, default: false},
        enableInlineEdit: {type: Boolean, default: false}
    },
    data() {
        return {
            icons: icons
        };
    },
    computed: {
        titleText() {
            return this.cleanText(this.menuData.titleText);
        },
        displayTitleText() {
            const text = this.titleText;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "Splash Screen Title Goes Here";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        bodyText() {
            return this.cleanText(this.menuData.bodyText);
        },
        displayBodyText() {
            const text = this.bodyText;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "This is where your splash screen description text will appear. You can provide instructions, information, or any other content you want to display to users when they see this screen.";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        placeholderText() {
            const text = this.menuData.placeholderText;
            return (text && text.trim()) || "THIS WILL BE AN OPTIONAL IMAGE OR PRESET ICON.";
        },
        hasTitle() {
            if (this.isStylePreview || this.isPreview || this.enableInlineEdit) {
                return true;
            }
            return !!(this.menuData.titleText && this.menuData.titleText.trim());
        },
        hasBody() {
            if (this.isStylePreview || this.isPreview || this.enableInlineEdit) {
                return true;
            }
            return !!(this.menuData.bodyText && this.menuData.bodyText.trim());
        },
        imageData() {
            return this.menuData.image || {
                imageType: 'icon',
                selectedIcon: null,
                imageUrl: null,
                zoom: 1,
                offsetX: 0,
                offsetY: 0
            };
        },
        buttons() {
            let buttons = [];

            if (this.menuData.buttons && Array.isArray(this.menuData.buttons) && this.menuData.buttons.length > 0) {
                buttons = this.menuData.buttons.map(btn => ({
                    text: btn.text || btn.buttonText || "",
                    enabled: btn.enabled !== false
                }));
            } else if (this.menuData.buttonLeft || this.menuData.buttonCenter || this.menuData.buttonRight) {
                buttons = [
                    {
                        text: this.menuData.buttonLeft?.buttonText || "",
                        enabled: this.menuData.buttonLeft?.enabled !== false
                    },
                    {
                        text: this.menuData.buttonCenter?.buttonText || "Button Center",
                        enabled: this.menuData.buttonCenter?.enabled !== false
                    },
                    {
                        text: this.menuData.buttonRight?.buttonText || "",
                        enabled: this.menuData.buttonRight?.enabled !== false
                    }
                ];
            } else {
                buttons = [
                    {text: "", enabled: false},
                    {text: "Button Center", enabled: true},
                    {text: "", enabled: false}
                ];
            }

            if (this.isStylePreview) {
                return buttons.map((btn, index) => {
                    if (!btn.text || !btn.text.trim()) {
                        const defaultTexts = ["Option 1", "Continue", "Option 3"];
                        return {
                            ...btn,
                            text: defaultTexts[index] || "Button"
                        };
                    }
                    return btn;
                });
            }

            return buttons;
        },
        buttonText0() {
            return this.cleanText(this.menuData.buttonLeft?.buttonText || this.menuData.buttons?.[0]?.text || this.menuData.buttons?.[0]?.buttonText);
        },
        displayButtonText0() {
            return this.getDisplayText(this.buttonText0);
        },
        buttonText1() {
            return this.cleanText(this.menuData.buttonCenter?.buttonText || this.menuData.buttons?.[1]?.text || this.menuData.buttons?.[1]?.buttonText);
        },
        displayButtonText1() {
            return this.getDisplayText(this.buttonText1);
        },
        buttonText2() {
            return this.cleanText(this.menuData.buttonRight?.buttonText || this.menuData.buttons?.[2]?.text || this.menuData.buttons?.[2]?.buttonText);
        },
        displayButtonText2() {
            return this.getDisplayText(this.buttonText2);
        },
        showBackButton() {
            return this.menuData.backButton?.enabled !== false;
        },

        densityVars() {
            if (!this.isStylePreview) return {};
            return {
                "--base-font": "0.6rem",
                "--title-scale": "1.9",
                "--body-scale": "1.5",
                "--btn-scale": "1.2",
                "--max-width-scale": "0.6",
                "--element-scale": "0.7",
                "--gap-scale": "0.8",
                "--image-scale": "0.5"
            };
        },
        centerWrapperStyles() {
            return {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100%',
                width: '100%'
            };
        },
        frameStyles() {
            const maxWidth = this.isStylePreview ? "calc(980px * var(--max-width-scale, 1))" : "980px";
            const columnGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const rowGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const margin = this.isStylePreview ? "calc(16px * var(--gap-scale, 1)) auto" : "16px auto";
            const backBtnCol = this.isStylePreview ? "calc(66px * var(--element-scale, 1))" : "75px";
            return {
                display: "grid",
                gridTemplateColumns: `${backBtnCol} 1fr`,
                columnGap: columnGap,
                rowGap: rowGap,
                width: "100%",
                maxWidth: maxWidth,
                margin: margin
            };
        },

        backBtnStyles() {
            const width = this.isStylePreview ? "calc(66px * var(--element-scale, 1))" : "75px";
            const height = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "56px";
            const fontSize = this.isStylePreview ? "calc(24px * var(--element-scale, 1))" : "24px";
            return {
                width: width,
                height: height,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: fontSize,
                fontWeight: 'bold',
                border: '3px solid',
                boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
                transition: 'background-color .18s ease, box-shadow .18s ease',
                backgroundColor: this.rgba(this.effectiveStyle.buttonBackgroundColor),
                color: this.rgba(this.effectiveStyle.textColor, false),
                borderColor: this.rgba(this.effectiveStyle.outlineColor, false),
                '--hover-bg': this.rgba(this.effectiveStyle.buttonHoverColor)
            };
        },
        backIconStyles() {
            return this.getIconStylesFromSvg(Back2SVG);
        },
        backIconColor() {
            const effective = this.getEffectiveStyle();
            return this.rgba(effective.textColor, false);
        },

        cardStyles() {
            const padding = this.isStylePreview ? "calc(22px * var(--element-scale, 1))" : "22px";
            return {
                gridColumn: "2 / -1",
                width: "100%",
                borderRadius: "18px",
                border: "3px solid",
                padding: padding,
                boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
                backgroundColor: this.rgba(this.effectiveStyle.titleScreenBackgroundColor),
                borderColor: this.rgba(this.effectiveStyle.outlineColor, false)
            };
        },

        cardGridStyles() {
            const gap = this.isStylePreview ? "calc(20px * var(--gap-scale, 1))" : "20px";
            const imageWidth = this.isStylePreview ? "calc(240px * var(--image-scale, 1))" : "240px";
            return {
                display: 'grid',
                gridTemplateColumns: `minmax(0, 1fr) ${imageWidth}`,
                gap: gap,
                alignItems: 'start'
            };
        },
        titleStyles() {
            const margin = this.isStylePreview ? "calc(2px * var(--gap-scale, 1)) 0 calc(16px * var(--gap-scale, 1)) 0" : "2px 0 16px 0";
            const paddingBottom = this.isStylePreview ? "calc(8px * var(--gap-scale, 1))" : "8px";
            const lineHeight = 1.4;
            const fontSize = 'calc(var(--base-font) * var(--title-scale))';
            return {
                margin: margin,
                paddingBottom: paddingBottom,
                fontWeight: 800,
                fontSize: fontSize,
                lineHeight: lineHeight,
                letterSpacing: '0.4px',
                color: this.rgba(this.effectiveStyle.titleColor, false),
                position: 'relative'
            };
        },
        bodyStyles() {
            const lineHeight = 1.65;
            const fontSize = "calc(var(--base-font) * var(--body-scale))";
            return {
                margin: 0,
                fontSize: fontSize,
                lineHeight: lineHeight,
                color: this.rgba(this.effectiveStyle.textColor, false),
                minHeight: `calc(${fontSize} * ${lineHeight} * 7)`,
                maxHeight: `calc(${fontSize} * ${lineHeight} * 7)`
            };
        },

        placeholderStyles() {
            const width = this.isStylePreview ? "calc(240px * var(--image-scale, 1))" : "240px";
            const height = this.isStylePreview ? "calc(240px * var(--image-scale, 1))" : "240px";
            return {
                borderRadius: '6px',
                width: width,
                height: height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: 'transparent',
                alignSelf: 'center',
                position: 'relative',
                overflow: 'hidden',
                marginTop: 'auto',
                marginBottom: 'auto'
            };
        },
        placeholderTextStyles() {
            const fontSize = this.isStylePreview ? "calc(13px * var(--element-scale, 1))" : "13px";
            return {
                fontSize: fontSize,
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: '1.1px',
                lineHeight: '1.3',
                fontFamily: 'ui-monospace, monospace',
                color: 'rgba(243,173,198,0.95)'
            };
        },

        buttonsRowStyles() {
            const gap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const marginTop = this.isStylePreview ? "calc(8px * var(--gap-scale, 1))" : "8px";
            return {
                gridColumn: '2 / -1',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: gap,
                marginTop: marginTop
            };
        },
        effectiveStyle() {
            const d = {
                titleScreenBackgroundColor: {r: 44, g: 62, b: 80, a: 1},
                titleColor: {r: 255, g: 255, b: 255, a: 1},
                textColor: {r: 235, g: 244, b: 248, a: 0.95},
                iconColor: {r: 235, g: 244, b: 248, a: 0.95},
                buttonBackgroundColor: {r: 33, g: 122, b: 145, a: 0.95},
                buttonHoverColor: {r: 52, g: 192, b: 32, a: 1},
                buttonTextColor: {r: 255, g: 255, b: 255, a: 1},
                outlineColor: {r: 255, g: 255, b: 255, a: 0.9},
                opacity: 1.0
            };
            return {...d, ...this.styleData};
        },
        iconDisplayStyles() {
            const size = this.isStylePreview ? "calc(180px * var(--image-scale, 1))" : "180px";
            return {
                fontSize: '180px !important',
                color: this.rgba(this.effectiveStyle.iconColor, false),
                width: '180px',
                height: '180px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            };
        }
    },
    watch: {
        displayTitleText(newVal) {
            this.syncEditableText('titleElement', newVal);
        },
        displayBodyText(newVal) {
            this.syncEditableText('bodyElement', newVal);
        },
        displayButtonText0(newVal) {
            this.syncButtonText(0, newVal);
        },
        displayButtonText1(newVal) {
            this.syncButtonText(1, newVal);
        },
        displayButtonText2(newVal) {
            this.syncButtonText(2, newVal);
        }
    },
    methods: {
        getIconComponent(iconValue) {
            return this.icons[iconValue] || null;
        },
        getButtonStyles(btn) {
            const minH = this.isStylePreview ? "calc(58px * var(--element-scale, 1))" : "58px";
            const pad = this.isStylePreview ? "0 calc(22px * var(--element-scale, 1))" : "0 22px";
            const radius = this.isStylePreview ? "14px" : "14px";

            const hidden = this.isButtonHidden(btn);

            return {
                minHeight: minH,
                padding: pad,
                borderRadius: radius,
                border: "3px solid",
                fontSize: "calc(var(--base-font) * var(--btn-scale))",
                fontWeight: 800,
                letterSpacing: "0.3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                cursor: hidden ? "default" : "pointer",
                boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                backgroundColor: this.rgba(this.effectiveStyle.buttonBackgroundColor),
                color: this.rgba(this.effectiveStyle.buttonTextColor, false),
                WebkitTextFillColor: this.rgba(this.effectiveStyle.buttonTextColor, false),
                borderColor: this.rgba(this.effectiveStyle.outlineColor, false),
                textShadow: "0 1px 0 rgba(0,0,0,0.35)",
                transition: "background-color .18s ease, box-shadow .18s ease",
                "--hover-bg": this.rgba(this.effectiveStyle.buttonHoverColor),
                visibility: hidden ? "hidden" : "visible",
                pointerEvents: hidden ? "none" : "auto"
            };
        },
        getImagePlaceholderStyle() {
            const baseStyle = this.placeholderStyles;

            const hasIcon = this.imageData.imageType === 'icon' && this.imageData.selectedIcon;
            const hasImage = (this.imageData.imageType === 'image' || this.imageData.imageType === 'resource') && this.imageData.imageUrl;

            if (hasImage) {
                const zoom = this.imageData.zoom || 1;
                const offsetX = this.imageData.offsetX || 0;
                const offsetY = this.imageData.offsetY || 0;

                return {
                    ...baseStyle,
                    backgroundImage: `url(${this.imageData.imageUrl})`,
                    backgroundSize: `${zoom * 100}%`,
                    backgroundPosition: `${50 + offsetX}% ${50 - offsetY}%`,
                    backgroundRepeat: 'no-repeat',
                    border: 'none'
                };
            }

            if (hasIcon) {
                return {
                    ...baseStyle,
                    border: 'none'
                };
            }

            return {
                ...baseStyle,
                border: '2px solid rgba(245, 164, 183, 0.5)'
            };
        },

        updateTitle(event) {
            this.updateTextField('titleText', event);
        },

        updateBody(event) {
            this.updateTextField('bodyText', event);
        },

        syncButtonText(index, newVal) {
            if (this.enableInlineEdit) {
                this.$nextTick(() => {
                    const buttonRef = this.$refs[`buttonRef${index}`];
                    const buttonEl = Array.isArray(buttonRef) ? buttonRef[0] : buttonRef;
                    if (buttonEl) {
                        const currentText = buttonEl.textContent || '';
                        const cleanedCurrent = this.cleanText(currentText);
                        const cleanedNew = this.cleanText(newVal);
                        
                        if (cleanedCurrent !== cleanedNew) {
                            const textEl = buttonEl.querySelector('.btn-text');
                            if (textEl) {
                                textEl.textContent = cleanedNew || ' ';
                            } else {
                                buttonEl.textContent = cleanedNew || ' ';
                            }
                        }
                    }
                });
            }
        },
        updateButton(index, event) {
            const element = event.target;
            const rawText = element.textContent || '';
            const newText = this.cleanText(rawText);

            if (!newText) {
                const fallbackValue = '\u200B';
                element.textContent = fallbackValue;
                const textEl = element.querySelector('.btn-text');
                if (textEl) textEl.textContent = ' ';
                this.updateButtonData(index, fallbackValue);
                return;
            }

            const buttonTexts = [this.buttonText0, this.buttonText1, this.buttonText2];
            const currentValue = buttonTexts[index];
            
            if (newText !== currentValue) {
                this.updateButtonData(index, newText);
            }
        },
        updateButtonData(index, text) {
            const buttonKeys = ['buttonLeft', 'buttonCenter', 'buttonRight'];
            const buttonKey = buttonKeys[index];
            const hasButtonKeys = this.menuData.buttonLeft || this.menuData.buttonCenter || this.menuData.buttonRight;
            
            if (buttonKey && (this.menuData[buttonKey] !== undefined || hasButtonKeys)) {
                this.$emit('update:menu-data', {
                    ...this.menuData,
                    [buttonKey]: {
                        ...(this.menuData[buttonKey] || {}),
                        buttonText: text
                    }
                });
            } else if (this.menuData.buttons && Array.isArray(this.menuData.buttons)) {
                this.$emit('update:menu-data', {
                    ...this.menuData,
                    buttons: this.menuData.buttons.map((btn, i) => 
                        i === index ? {...btn, text, buttonText: text} : btn
                    )
                });
            }
        },


        handleButtonClick(index, button) {
            if (this.enableInlineEdit) {
                const buttonRef = this.$refs[`buttonRef${index}`];
                const buttonEl = Array.isArray(buttonRef) ? buttonRef[0] : buttonRef;
                if (buttonEl && buttonEl.contentEditable === 'true') {
                    buttonEl.focus();
                }
                return;
            }
            
            if (this.isStylePreview || this.isPreview) {
                return;
            }

            this.$emit('button-clicked', index, button);

            if (button.onClick && typeof button.onClick === 'function') {
                button.onClick();
            }

            if (button.action && typeof button.action === 'function') {
                button.action();
            }
        },

        handleImageClick() {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = (e) => {
                const file = e.target.files[0];
                if (!file) return;

                if (file.type && file.type.startsWith('image/')) {
                    this.$emit('upload-image', file);
                }
                input.value = '';
            };
            input.click();
        }
    }
};
</script>

<style scoped>
.splash-screen {
    width: 100%;
    height: 100%;
    background: transparent;
    box-sizing: border-box;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.splash-screen {
    --base-font: 1rem;
    --title-scale: 1.75;
    --body-scale: 1.0;
    --btn-scale: 1.0;
    --max-width-scale: 1.0;
    --element-scale: 1.0;
    --gap-scale: 1.0;
    --image-scale: 1.0;
}

.center-wrapper {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.frame {
    box-sizing: border-box;
}

.card {
    box-sizing: border-box;
    overflow: hidden;
}

.card-grid {
    box-sizing: border-box;
}

.placeholder {
    box-sizing: border-box;
    flex-shrink: 0;
}

.splash-title {
    display: block;
    position: relative;
}

.v-application .title {
    font-size: inherit;
}

.splash-title::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: currentColor;
    border-radius: 2px;
}

.btn {
    position: relative;
}

.btn:not(:last-child)::after {
    content: "";
    position: absolute;
    right: calc(-1 * calc(20px * var(--gap-scale, 1)) / 2);
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 60%;
    background: rgba(255, 255, 255, 0.3);
}

.splash-screen .btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.splash-screen .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.splash-screen:not(.no-hover) .btn:hover {
    background-color: var(--hover-bg) !important;
}

.splash-screen .back-btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.splash-screen .back-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.splash-screen:not(.no-hover) .back-btn:hover {
    background-color: var(--hover-bg) !important;
}

.placeholder:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.35) inset;
}

.icon-display {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
}

.back-icon-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.back-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0; /* Hide the actual SVG, we'll use the mask instead */
}

.image-display {
    height: 100%;
    width: 100%;
}

@media (max-width: 900px) {
    .card-grid {
        grid-template-columns: 1fr !important;
    }

    .buttons {
        grid-template-columns: 1fr !important;
    }
}

.editable-text.is-editable {
    cursor: text;
    position: relative;
    outline: none;
}

.editable-text.is-editable:hover {
    background-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.4);
}

.editable-text.is-editable:focus {
    background-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.7);
}

.splash-title {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    position: relative !important;
}

.body {
    display: -webkit-box !important;
    -webkit-line-clamp: 9 !important;
    line-clamp: 9 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    max-height: 12em !important;
}

.btn {
    min-height: 58px !important;
    max-height: none !important;
    flex: 1 1 0 !important;
    max-width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    word-break: break-word !important;
    white-space: normal !important;
}

.btn .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.btn[contenteditable="true"] {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.btn[contenteditable="true"] .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.image-clickable {
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
}

.image-clickable:hover {
    opacity: 0.95;
}

.image-clickable:not(:has(.image-display)):hover::before {
    content: 'Click to upload image';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(52, 192, 32, 0.9);
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    z-index: 10;
    pointer-events: none;
}

.image-clickable:has(.image-display):hover::after {
    content: 'Click to change';
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(52, 192, 32, 0.9);
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    z-index: 10;
    pointer-events: none;
}

</style>
